import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import * as Sentry from "@sentry/react";
import { RefreshIcon } from "@heroicons/react/solid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSwappingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TextEditor from "@/components/forms/TextEditor";
import UploadImages from "@/components/forms/upload";
import getAcquisitionToken from "../../../../src/utils/getAcquisitionToken";
import { updateProperty, useProperty } from "@/_actions/propertiesActions";
import config from "../../properties/forms/upload_media/uppyConfigPro";
import PropTypes from "prop-types";
import { usePostHog } from "posthog-js/react";

const DescriptionSchema = Yup.object().shape({
  description: Yup.string().required("Campo requerido"),
  images: Yup.array()
    .min(1, "Debes agregar al menos una imagen")
    .required("Debes agregar al menos una imagen"),
});

const SortableArea = ({ items, onSortEnd, deleteImage }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 50,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      activationConstraint: {
        delay: 50,
        distance: 50,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        distance: 50,
      },
    }),
  );
  SortableArea.propTypes = {
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
    onSortEnd: PropTypes.func.isRequired,
    deleteImage: PropTypes.func.isRequired,
  };

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active?.id && over?.id && active?.id !== over?.id) {
      const oldIndex = items.findIndex((e) => e?.id === active.id);
      const newIndex = items.findIndex((e) => e?.id === over.id);
      onSortEnd(oldIndex, newIndex);
    }
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-7">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={rectSwappingStrategy}>
          {items.map((item, index) => (
            <div key={item.id}>
              <SortableItem id={item.id} value={item} index={index} deleteImage={deleteImage} />
            </div>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

const SortableItem = ({ id, value, index, deleteImage }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative w-full cursor-move touch-pinch-zoom select-none list-none bg-transparent focus:shadow-xl"
    >
      <div className="group w-full overflow-hidden rounded-lg bg-transparent focus-within:ring-2 md:block">
        <div className="aspect-square bg-transparent">
          <img
            src={value.url || value}
            className="pointer-events-none h-full w-full object-cover group-hover:opacity-75"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            deleteImage(index, id);
          }}
          className="w-full items-center rounded-b-md border px-4 py-2 text-center text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2"
        >
          <div className="pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="m-auto -ml-1 h-5 w-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};
SortableItem.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  deleteImage: PropTypes.func.isRequired,
};

const ImageUploadField = ({ push, propertyId }) => {
  const [removeId, setRemoveId] = useState(null);

  const uppyConfig = useMemo(() => {
    return config({ callBack: push, propertyId: propertyId });
  }, [propertyId, push]);

  return (
    <UploadImages
      removeId={removeId}
      uppyConfiguration={uppyConfig}
      height={200}
      showSelectedFiles={false}
    />
  );
};
ImageUploadField.propTypes = {
  push: PropTypes.func.isRequired,
  propertyId: PropTypes.string.isRequired,
};

export default function form() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const propertyId = localStorage.getItem("CYTpropertyUuid");
  const { data: propertyData = {} } = useProperty(propertyId);

  useEffect(() => {
    const token = getAcquisitionToken();
    if (!token) {
      navigate("/unirse/particular");
    }
    if (!localStorage.getItem("CYTpropertyUuid")) {
      navigate("/unirse/ubicacion-precio");
    }
  }, [navigate]);

  const onSubmit = async (values, actions) => {
    const { setSubmitting } = actions;
    setSubmitting(true);

    try {
      const cleanString = (str) => {
        if (typeof str === "string") {
          return str.replace(/^"(.*)"$/, "$1");
        }
        return str;
      };

      const processedValues = {
        ...values,
        status: "pending",
        images: values.images.map((img) => {
          if (img && img.url) {
            return {
              ...img,
              url: cleanString(img.url),
              id: img.id ? cleanString(img.id) : img.id,
            };
          } else {
            return img;
          }
        }),
      };

      await updateProperty(propertyId, processedValues);
      if (posthog) {
        posthog.capture("Signup - Particular", {
          step: 4,
          stepName: "description",
          imagesCount: processedValues.images?.length || 0,
        });
      }
      setSubmitting(false);
      navigate("/unirse/seleccionar-plan");
    } catch (err) {
      setSubmitting(false);
      Sentry.captureException(err);
      console.error("Error en submit:", err);
    }
  };

  return (
    <Formik
      onSubmit={onSubmit}
      enableReinitialize={true}
      initialValues={{
        description: propertyData?.description || "",
        images: propertyData?.images || [],
      }}
      validationSchema={DescriptionSchema}
    >
      {({
        values,

        isSubmitting,
      }) => (
        <Form className="w-full space-y-6">
          <div>
            <label htmlFor="description" className="form-title">
              Descripción
            </label>
            <TextEditor
              name="description"
              className="input"
              placeholder="Texto sobre ubicación, amenidades, cosas que hay alrededor, etc."
            />
            <ErrorMessage
              name="description"
              render={(msg) => <div className="text-sm text-red-600">{msg}</div>}
            />
          </div>

          <div>
            <label htmlFor="images" className="form-title">
              Fotos
            </label>
            <FieldArray name="images">
              {({ move, push, remove }) => (
                <div className="space-y-3 p-3">
                  <ImageUploadField push={push} propertyId={propertyId} />

                  {values?.images?.length > 0 && (
                    <p className="form-title">Arrastra y define el orden de las imágenes</p>
                  )}

                  <SortableArea
                    items={values.images}
                    deleteImage={(index, id) => {
                      remove(index);
                    }}
                    onSortEnd={move}
                  />
                </div>
              )}
            </FieldArray>
            <ErrorMessage
              name="images"
              render={(msg) => <div className="text-sm text-red-600">{msg}</div>}
            />
          </div>

          <div className="sticky bottom-0 z-50 grid w-full grid-cols-2 bg-white p-3 md:relative md:bg-transparent md:p-5">
            <a href="/unirse/amenidades" className="self-center underline">
              Regresar
            </a>
            <div className="sticky bottom-0 bg-white p-2 pb-7 md:block md:p-0">
              <button
                disabled={isSubmitting}
                type="submit"
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-black py-2 px-4 text-lg font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <RefreshIcon className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                    <span className="ml-2">Procesando...</span>
                  </div>
                ) : (
                  <>
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <svg
                        className="h-5 w-5 text-black group-hover:text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    Continuar
                  </>
                )}
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
